/**
 * Lightweight leak probes for the non-functional fit tests.
 *
 * Disabled by default. They only install when the boolean env flag
 * `VITE_ENABLE_PERF_PROBES` ("true"/"1") is set, so production never pays for
 * the wrappers. When enabled they expose `window.__perfProbes.snapshot()` with
 * the number of still-open peer connections, still-live media tracks and
 * pending animation frames.
 */

export interface PerfProbeSnapshot {
  enabled: boolean;
  openPeerConnections: number;
  liveMediaTracks: number;
  activeAnimationFrames: number;
}

declare global {
  interface Window {
    /** Installed by `installPerfProbes` only when the env flag is set. */
    __perfProbes?: { snapshot(): PerfProbeSnapshot };
  }
}

const flag = String(
  import.meta.env.VITE_ENABLE_PERF_PROBES ?? "",
).toLowerCase();
export const perfProbesEnabled = flag === "true" || flag === "1";

const disabledSnapshot: PerfProbeSnapshot = {
  enabled: false,
  openPeerConnections: 0,
  liveMediaTracks: 0,
  activeAnimationFrames: 0,
};

export function installPerfProbes() {
  if (!perfProbesEnabled || typeof window === "undefined") return;
  if (window.__perfProbes) return;

  const peers = new Set<RTCPeerConnection>();
  const tracks = new Set<MediaStreamTrack>();
  const pendingFrames = new Set<number>();

  // --- peer connections -----------------------------------------------------
  const NativePeerConnection = window.RTCPeerConnection;
  if (NativePeerConnection) {
    // A `function` that returns a fresh instance is the classic constructor
    // monkey-patch; its shape cannot be written as `typeof RTCPeerConnection`
    // (static members, construct signature), so this one crossing is asserted.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const Patched = function (
      this: unknown,
      ...args: ConstructorParameters<typeof RTCPeerConnection>
    ) {
      const pc = new NativePeerConnection(...args);
      peers.add(pc);
      const forget = () => peers.delete(pc);
      const nativeClose = pc.close.bind(pc);
      pc.close = () => {
        forget();
        nativeClose();
      };
      pc.addEventListener("connectionstatechange", () => {
        if (pc.connectionState === "closed") forget();
      });
      return pc;
    } as unknown as typeof RTCPeerConnection;
    Patched.prototype = NativePeerConnection.prototype;
    window.RTCPeerConnection = Patched;
  }

  // --- media tracks ---------------------------------------------------------
  const watchTracks = (stream: MediaStream) => {
    for (const track of stream.getTracks()) {
      tracks.add(track);
      const nativeStop = track.stop.bind(track);
      track.stop = () => {
        tracks.delete(track);
        nativeStop();
      };
      track.addEventListener("ended", () => tracks.delete(track));
    }
    return stream;
  };

  const media = navigator.mediaDevices;
  if (media) {
    const nativeGetUserMedia = media.getUserMedia?.bind(media);
    if (nativeGetUserMedia) {
      media.getUserMedia = async (constraints?: MediaStreamConstraints) =>
        watchTracks(await nativeGetUserMedia(constraints));
    }
    const nativeGetDisplayMedia = media.getDisplayMedia?.bind(media);
    if (nativeGetDisplayMedia) {
      media.getDisplayMedia = async (constraints?: DisplayMediaStreamOptions) =>
        watchTracks(await nativeGetDisplayMedia(constraints));
    }
  }

  // --- animation frames -----------------------------------------------------
  const nativeRaf = window.requestAnimationFrame.bind(window);
  const nativeCancel = window.cancelAnimationFrame.bind(window);
  window.requestAnimationFrame = (callback: FrameRequestCallback) => {
    const id = nativeRaf((time) => {
      pendingFrames.delete(id);
      callback(time);
    });
    pendingFrames.add(id);
    return id;
  };
  window.cancelAnimationFrame = (id: number) => {
    pendingFrames.delete(id);
    nativeCancel(id);
  };

  window.__perfProbes = {
    snapshot: (): PerfProbeSnapshot => ({
      enabled: true,
      openPeerConnections: [...peers].filter(
        (pc) => pc.connectionState !== "closed",
      ).length,
      liveMediaTracks: [...tracks].filter(
        (track) => track.readyState === "live",
      ).length,
      activeAnimationFrames: pendingFrames.size,
    }),
  };
}

export function readPerfProbes(): PerfProbeSnapshot {
  if (typeof window === "undefined") return disabledSnapshot;
  return window.__perfProbes?.snapshot() ?? disabledSnapshot;
}
